import type { Issue } from './types';
import type { Project } from './projects';

/**
 * A SPRINT: A STRETCH OF TIME WITH A GOAL, AND THE WORK PUT INTO IT.
 *
 * Stage 1 of *Working in sprints* in ROADMAP.md. A board either runs on flow —
 * "what is the single best thing to start now", which is what it does today —
 * or in timeboxes, where the question becomes "what is left in this one".
 *
 * WHY THE RULES ARE IN THEIR OWN FILE rather than in the screens that use them.
 * The same three questions get asked from the board, the backlog, Next up and
 * every future chart: which sprint is running, what is in it, and how much time
 * is left. Four copies of "the active one, unless it has ended" is four places
 * to disagree, and the two progress percentages in this codebase already proved
 * that they will — they disagreed for weeks and both looked plausible.
 *
 * DATES ARE PLAIN YYYY-MM-DD STRINGS, not milliseconds, and this is deliberate
 * throughout. A sprint runs "the 13th to the 27th"; it does not run to a moment.
 * Stored as a moment, a fortnight starting in Trinidad ends a day early for
 * somebody reading in London. As ISO strings they also sort and compare with
 * `<` and `>` correctly, which is the small dividend that makes it painless.
 */
export interface Sprint {
  id: string;
  name: string;
  /** What this stretch is for, in a sentence. Optional on purpose — see below. */
  goal?: string;
  startsOn: string; // YYYY-MM-DD
  endsOn: string; // YYYY-MM-DD
  state: SprintState;
  /**
   * WHEN IT ACTUALLY BEGAN AND ACTUALLY ENDED, which is not when it was planned
   * to. A sprint planned for Monday and started on Wednesday is two days
   * shorter, and a burndown drawn from `startsOn` opens with two days of
   * imaginary flatness before anybody had started. Set once, never rewritten.
   */
  startedAt?: number;
  closedAt?: number;
  /**
   * THE END IT WAS PLANNED FOR WHEN IT WENT ACTIVE, `YYYY-MM-DD`, or absent until
   * it starts. `endsOn` can be edited mid-sprint as scope moves; this is
   * snapshotted once at the start and never rewritten, so "finished late against
   * what we committed to" survives an extension (closedAt vs this). Written by
   * the database — migration 0097; the app only reads it. A plain day, like
   * endsOn, so it stays out of the millisecond convention.
   */
  plannedEnd?: string | null;
  createdAt?: number;
  /**
   * WHEN IT WAS SENT FOR APPROVAL — #332, migration 0075.
   *
   * Not worked out from the signature rows, for the reason
   * `builds.signoffRequestedAt` exists: it is how you see that something has
   * been outstanding a fortnight, and a sheet of unsigned rows cannot tell you
   * whether it was raised this morning or in July.
   *
   * ⚠️ IT IS ALSO THE LATCH. A sprint that has been asked about is never asked
   * again — finishing, reopening and finishing a sprint must not send the same
   * people a second sheet.
   */
  signoffRequestedAt?: number | null;
}

/**
 * PLANNED, RUNNING, FINISHED — and no fourth.
 *
 * "Cancelled" is the one that gets suggested and it is deliberately absent: a
 * sprint abandoned halfway is a sprint that was run and did not go well, and
 * hiding it removes exactly the fortnight a retrospective needs to look at.
 * Closing it early with the work carried over says the same thing honestly.
 */
export type SprintState = 'planned' | 'active' | 'closed';

export const SPRINT_STATE_LABEL: Record<SprintState, string> = {
  planned: 'Planned',
  active: 'Running',
  closed: 'Finished',
};

/** How long a sprint is, unless somebody says otherwise. */
export const DEFAULT_SPRINT_DAYS = 14;

/** Whether this board works in timeboxes at all. */
export function worksInSprints(project: Pick<Project, 'cadence'> | undefined | null): boolean {
  return project?.cadence === 'sprints';
}

/* ── Dates, as strings ─────────────────────────────────────────────────────── */

/** Today as YYYY-MM-DD, in the reader's own timezone rather than UTC. */
export function today(now: number = Date.now()): string {
  const d = new Date(now);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/**
 * A date a number of days after another one.
 *
 * Built through `Date` rather than by adding to a number of milliseconds,
 * because a fortnight that crosses a daylight-saving change is 13 days and 23
 * hours — and arithmetic on milliseconds lands on the day before, once or twice
 * a year, in a way that is very hard to see in a screenshot.
 */
export function addDays(date: string, days: number): string {
  const [y, m, d] = date.split('-').map(Number);
  const at = new Date(y, (m ?? 1) - 1, d ?? 1);
  at.setDate(at.getDate() + days);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${at.getFullYear()}-${pad(at.getMonth() + 1)}-${pad(at.getDate())}`;
}

/**
 * Whole days from one date to another, end inclusive.
 *
 * INCLUSIVE BECAUSE A ONE-DAY SPRINT IS ONE DAY. The exclusive reading makes it
 * zero, which is both wrong to a reader and a division by zero in any chart that
 * plots progress against elapsed time.
 */
export function daysBetween(from: string, to: string): number {
  const at = (s: string) => {
    const [y, m, d] = s.split('-').map(Number);
    return new Date(y, (m ?? 1) - 1, d ?? 1).getTime();
  };
  return Math.round((at(to) - at(from)) / 86_400_000) + 1;
}

/** How long a sprint runs for, in days. */
export const sprintLength = (s: Pick<Sprint, 'startsOn' | 'endsOn'>) =>
  daysBetween(s.startsOn, s.endsOn);

/**
 * DAYS LEFT, AND IT CAN BE NEGATIVE.
 *
 * A sprint that ran past its end date is a real and common thing, and clamping
 * to zero is how a board comes to say "0 days left" for a fortnight — which
 * reads as "due today, every day" rather than "this has overrun". The number
 * that says something is wrong is the number worth keeping.
 */
export function daysLeft(s: Pick<Sprint, 'endsOn'>, now: number = Date.now()): number {
  return daysBetween(today(now), s.endsOn) - 1;
}

/** "4 days left", "ends today", "3 days over" — the phrase, not the number. */
export function timeLeft(s: Pick<Sprint, 'endsOn'>, now: number = Date.now()): string {
  const n = daysLeft(s, now);
  if (n === 0) return 'ends today';
  if (n === 1) return '1 day left';
  if (n > 1) return `${n} days left`;
  const over = -n;
  return over === 1 ? '1 day over' : `${over} days over`;
}

/* ── Which one is running ──────────────────────────────────────────────────── */

/**
 * THE SPRINT THAT IS RUNNING, or nothing.
 *
 * There can only be one — the database holds that with a unique index rather
 * than trusting four call sites to check — so this reads the state and does not
 * guess from the dates. A sprint whose end date has passed and which nobody has
 * closed is STILL RUNNING, and that is the honest answer: the work is still in
 * it, the board should still show it, and the overrun should be visible rather
 * than silently tidied away by a date comparison.
 */
export function running(sprints: Sprint[]): Sprint | undefined {
  return sprints.find((s) => s.state === 'active');
}

/**
 * THE ORDER TO SHOW THEM IN: running first, then what is coming, then what is
 * done, newest first.
 *
 * That is the order of interest and it is not the order of any single field, so
 * it is written down once here rather than re-derived by each screen with a
 * slightly different sort.
 */
export function inOrder(sprints: Sprint[]): Sprint[] {
  const rank: Record<SprintState, number> = { active: 0, planned: 1, closed: 2 };
  return [...sprints].sort((a, b) => {
    const r = rank[a.state] - rank[b.state];
    if (r) return r;
    // Coming up: soonest first. Finished: most recent first.
    return a.state === 'closed'
      ? b.startsOn.localeCompare(a.startsOn)
      : a.startsOn.localeCompare(b.startsOn);
  });
}

/** The work in one sprint. Everything else is the backlog, including finished. */
export function inSprint(issues: Issue[], sprintId: string): Issue[] {
  return sprintId ? issues.filter((i) => (i.sprint ?? '') === sprintId) : [];
}

/** The work in no sprint at all — what planning pulls FROM. */
export function notInAnySprint(issues: Issue[]): Issue[] {
  return issues.filter((i) => !i.sprint);
}

/**
 * WHAT COULD BE PLANNED INTO A SPRINT: in no sprint, and not already resolved.
 *
 * `notInAnySprint` is every card without a sprint, FINISHED WORK INCLUDED, and
 * that is right for the question it answers — where does a deleted sprint's work
 * go. It is wrong for the question the planning list asks. On PhotoSyncApp the
 * two differ by 138 cards.
 */
/**
 * WHETHER WORK CAN STILL BE PUT INTO THIS SPRINT.
 *
 * Running or planned, never closed. Moving a card into a finished fortnight
 * rewrites what that fortnight is said to have contained — months after its
 * report was read and acted on, and with nothing on screen to say it changed.
 */
export function canPlanInto(s: Pick<Sprint, 'state'>): boolean {
  return s.state !== 'closed';
}

export function plannable(issues: Issue[]): Issue[] {
  return notInAnySprint(issues).filter((i) => i.status !== 'done' && i.status !== 'wontdo');
}

/**
 * THE LIST OF WORK OFFERED WHILE PLANNING, best first, narrowed by the box.
 *
 * HERE BECAUSE IT WAS WRONG IN THE SCREEN, TWICE. `readyFirst` both ranks and
 * DROPS — blocked work, and resolved work. Used as though it only ranked, it
 * produced a list that had lost cards while the count beside it had not:
 * "Showing 0 of 6" over nothing, which is what Vinnie found on UAT. The same
 * trap had already been fixed a hundred lines earlier for a sprint's contents
 * and left standing here, which is the argument for the rule living in one
 * testable place rather than in each screen that needs it.
 *
 * BLOCKED WORK IS OFFERED, RANKED LAST. Planning a fortnight is exactly when a
 * card waiting on something else gets pulled in — the blocker is expected to
 * clear inside it. Dropping it hides the work most likely to need planning
 * around from the only screen that plans.
 *
 * THE CAP IS STATED BY THE CALLER, not hidden here: a list that simply stops
 * reads as "that is everything", which is how work goes missing from a plan.
 */
export function offerFromBacklog(
  backlog: Issue[],
  find: string,
  rank: (issues: Issue[]) => Issue[],
  limit = 25
): { shown: Issue[]; matching: number } {
  const q = find.trim().toLowerCase();
  const matching = q
    ? backlog.filter((i) => i.title.toLowerCase().includes(q) || i.ref.toLowerCase().includes(q))
    : backlog;
  const ranked = rank(matching);
  const seen = new Set(ranked.map((i) => i.id));
  const rest = matching.filter((i) => !seen.has(i.id));
  return { shown: [...ranked, ...rest].slice(0, limit), matching: matching.length };
}

/**
 * WHAT WAS COMMITTED AND WHAT WAS ADDED AFTERWARDS.
 *
 * The say-do ratio a retrospective asks about is these two numbers, and burnup
 * exists precisely because burndown hides the second one: work added as fast as
 * it is finished draws a healthy-looking line down to zero.
 *
 * A SPRINT THAT HAS NOT STARTED HAS COMMITTED EVERYTHING IN IT, which is not a
 * special case so much as the definition — nothing can have been added
 * mid-flight before there was a flight. Written out rather than left to fall
 * out of the comparison, because an unstarted sprint has no `startedAt` and the
 * comparison would quietly put every card in `added`.
 */
export function committedAndAdded(
  issues: Issue[],
  sprint: Pick<Sprint, 'id' | 'startedAt'>
): { committed: Issue[]; added: Issue[] } {
  const mine = inSprint(issues, sprint.id);
  if (!sprint.startedAt) return { committed: mine, added: [] };
  const committed: Issue[] = [];
  const added: Issue[] = [];
  for (const i of mine) {
    /*
     * NO JOIN TIME MEANS IT WAS ALREADY THERE. Every issue that predates this
     * field has none, and calling those "added mid-sprint" would report a first
     * sprint that committed to nothing and then had everything dropped on it.
     */
    (i.addedToSprintAt && i.addedToSprintAt > sprint.startedAt ? added : committed).push(i);
  }
  return { committed, added };
}

/* ── Making one ────────────────────────────────────────────────────────────── */

/**
 * The dates to offer for the next sprint, so nobody types them.
 *
 * It starts the day after the last one ends — not today — because that is what
 * back-to-back fortnights mean, and a team planning on Friday for a sprint that
 * begins on Monday should not have to correct the form. With no previous
 * sprint it starts today, which is the only thing it can sensibly mean.
 */
export function suggestedDates(
  previous: Pick<Sprint, 'endsOn'> | undefined,
  now: number = Date.now(),
  days: number = DEFAULT_SPRINT_DAYS
): { startsOn: string; endsOn: string } {
  const startsOn = previous ? addDays(previous.endsOn, 1) : today(now);
  return { startsOn, endsOn: addDays(startsOn, days - 1) };
}

/**
 * The name to offer. "Sprint 4" if the last was "Sprint 3", and otherwise the
 * count plus one — teams that name their sprints after something else get their
 * own wording and lose nothing.
 */
export function suggestedName(sprints: Sprint[]): string {
  const numbered = sprints
    .map((s) => Number(s.name.match(/(\d+)\s*$/)?.[1]))
    .filter((n) => Number.isFinite(n)) as number[];
  const next = numbered.length ? Math.max(...numbered) + 1 : sprints.length + 1;
  return `Sprint ${next}`;
}

/**
 * WHAT STOPS A SPRINT BEING SAVED, in the words to show the person.
 *
 * Overlapping dates are refused, and that is the one worth defending. Two
 * sprints covering the same Tuesday makes "which sprint was this work in on
 * Tuesday" ambiguous, which is not a display problem — it is the axis every
 * burndown and every velocity chart is plotted against. Allowed once, it cannot
 * be corrected afterwards without deciding, card by card, which fortnight it
 * really belonged to.
 */
export function sprintProblem(
  draft: { name: string; startsOn: string; endsOn: string },
  existing: Pick<Sprint, 'id' | 'name' | 'startsOn' | 'endsOn'>[],
  editing?: string
): string {
  const name = draft.name.trim();
  if (!name) return 'Give the sprint a name.';
  if (name.length > 60) return 'That name is too long to sit above the board — keep it under 60 characters.';
  if (!draft.startsOn || !draft.endsOn) return 'A sprint needs a start and an end date.';
  if (draft.endsOn < draft.startsOn) return 'It cannot end before it starts.';
  const others = existing.filter((s) => s.id !== editing);
  if (others.some((s) => s.name.trim().toLowerCase() === name.toLowerCase()))
    return `There is already a sprint called ${name}.`;
  const clash = others.find((s) => draft.startsOn <= s.endsOn && s.startsOn <= draft.endsOn);
  if (clash)
    return `Those dates overlap ${clash.name} (${clash.startsOn} to ${clash.endsOn}). Two sprints covering the same day makes every chart ambiguous.`;
  if (daysBetween(draft.startsOn, draft.endsOn) > 90)
    return 'That is over three months. A timebox that long is a project, and nothing useful can be read off it.';
  return '';
}

/**
 * WHICH PARTS OF A SPRINT MAY STILL BE CHANGED.
 *
 * A sprint was write-once until 18 August: the planner created one and nothing
 * anywhere edited it, so a date typed wrongly was permanent and the only way
 * out was deleting the sprint and emptying it back into the backlog. The store
 * has had `editSprint` — validated, refusing overlaps — the whole time. No
 * screen ever called it.
 *
 * THE ANSWER DIFFERS BY STATE, and each difference is about what would become a
 * lie rather than about being careful:
 *
 *   · A START THAT HAS PASSED cannot move. The burndown counts from it, so
 *     moving it backwards invents days of work that nobody did and moving it
 *     forwards deletes days somebody spent.
 *   · AN END CAN MOVE WHILE THE SPRINT RUNS. "We need three more days" is the
 *     ordinary case, and the alternative — finish it early and plan another —
 *     splits one fortnight's history across two rows of every chart.
 *   · A NAME IS FIXED ONCE WORK IS IN FLIGHT, because a tracker push matches
 *     sprints BY NAME. Renaming a running one mid-fortnight makes the next push
 *     say the sprint it names is not on this board.
 *   · A FINISHED SPRINT IS A RECORD. Nothing about it is editable, including
 *     its goal: what a team said they were doing is the part worth keeping.
 */
export function editableParts(state: SprintState): {
  name: boolean;
  goal: boolean;
  startsOn: boolean;
  endsOn: boolean;
} {
  if (state === 'closed') return { name: false, goal: false, startsOn: false, endsOn: false };
  if (state === 'active') return { name: false, goal: true, startsOn: false, endsOn: true };
  return { name: true, goal: true, startsOn: true, endsOn: true };
}

/** Whether there is anything at all to offer an Edit button for. */
export function canEditSprint(state: SprintState): boolean {
  return Object.values(editableParts(state)).some(Boolean);
}

/* ── Closing one ───────────────────────────────────────────────────────────── */

/**
 * WHAT DID NOT GET FINISHED, and so has to go somewhere.
 *
 * "Not done" rather than "not done and not abandoned": a card marked won't-do
 * IS resolved — somebody decided against it — and dragging it into the next
 * fortnight would make every sprint accumulate the work nobody is ever doing.
 */
export function unfinishedIn(issues: Issue[], sprintId: string): Issue[] {
  return inSprint(issues, sprintId).filter((i) => i.status !== 'done' && i.status !== 'wontdo');
}

/**
 * THE SPRINT UNFINISHED WORK WOULD BE CARRIED INTO — the next planned one.
 *
 * THE EARLIEST PLANNED ONE, unambiguously: sprints may not overlap (see
 * `sprintProblem`), so "the next one" is a fact rather than a judgement.
 * Nothing planned means there is nowhere to carry to, which is a real state and
 * the caller has to say so rather than dropping the work somewhere.
 */
export function nextAfter(sprints: Sprint[]): Sprint | undefined {
  return sprints
    .filter((s) => s.state === 'planned')
    .sort((a, b) => a.startsOn.localeCompare(b.startsOn))[0];
}

/** What happens to work that did not get finished. */
export type Leftovers = 'carry' | 'backlog';

/**
 * WHAT CLOSING A SPRINT HAS TO ASK, IF ANYTHING.
 *
 * ASKED, NOT DECIDED HERE — Vinnie, 13 August: "why can't we have a pop-up that
 * asks the developer whether to carry it forward or send it to the backlog? Let
 * it be the user's choice." He is right, and the reason is that the two mean
 * genuinely different things afterwards rather than being a preference:
 * carrying says the work is still committed and the timebox ran out; returning
 * it to the backlog says the commitment lapsed with the sprint. Velocity, the
 * sprint report and the say-do ratio all read differently depending which is
 * true, and only the team closing the sprint knows which one it was.
 *
 * So the software's job is to make sure the question is ASKED — silently doing
 * either one is what puts a wrong number on a chart months later — and to say
 * plainly when one of the answers is not available.
 *
 * `ask: false` when nothing is unfinished. There is no question, so there is no
 * dialog: a confirmation that can only be answered one way trains people to
 * press through the ones that matter.
 */
export function closingAsks(
  sprint: Sprint,
  all: Sprint[],
  issues: Issue[]
): { ask: boolean; left: Issue[]; into?: Sprint } {
  const left = unfinishedIn(issues, sprint.id);
  return { ask: left.length > 0, left, into: nextAfter(all) };
}

/* ── What a tracker file says ──────────────────────────────────────────────── */

/**
 * A SPRINT NAME OUT OF A TRACKER FILE, TURNED INTO THE SPRINT IT MEANS.
 *
 * WHY A NAME AND NOT AN ID. The file is written by an assistant, or by a person,
 * in a text editor. Nobody types a uuid, and a format that required one could
 * only be filled in by copying from a screen — which is the opposite of a file
 * an assistant can keep on its own.
 *
 * AN UNKNOWN NAME IS REFUSED, NOT CREATED — Vinnie, 13 August. A tracker that
 * could invent fortnights is one where a typo silently starts a sprint nobody
 * planned, and where an assistant reorganises a real team's timeboxes without
 * anybody deciding to. Planning a sprint stays a thing a person does on the
 * board; the file may only say which of the existing ones a card is in.
 *
 * REFUSED RATHER THAN DROPPED, which is the same argument as `checkedMembers`
 * in src/projects.ts: a value quietly discarded looks exactly like a field
 * nobody filled in, and is found weeks later. This returns what it could not
 * match so the caller can say so out loud.
 *
 * MATCHED LOOSELY ON CASE AND SPACE, because "sprint 4" and "Sprint 4" are the
 * same fortnight to everyone except a string comparison.
 */
export function sprintNamed(name: string, sprints: Sprint[]): Sprint | undefined {
  const want = name.trim().toLowerCase();
  return want ? sprints.find((s) => s.name.trim().toLowerCase() === want) : undefined;
}

/**
 * Every sprint name a tracker used that this board has never heard of.
 *
 * Returned as the distinct names rather than one per card: a file that puts
 * forty items in "Sprint 5" has one problem, and reporting it forty times
 * buries the second problem underneath it.
 */
export function unknownSprintNames(
  items: { sprintName?: string }[],
  sprints: Sprint[]
): string[] {
  /*
   * KEYED ON THE TIDIED NAME, REPORTED AS IT WAS WRITTEN. Deduplicating on the
   * raw text would list "Sprint 9" and "sprint 9" as two separate problems, when
   * they are one typo made twice — and the person fixing it is scanning their
   * file for what the message says, so the message has to say what they wrote.
   * Same reasoning as `checkedMembers` in src/projects.ts.
   */
  const missing = new Map<string, string>();
  for (const i of items) {
    const named = (i.sprintName ?? '').trim();
    if (!named || sprintNamed(named, sprints)) continue;
    const key = named.toLowerCase();
    if (!missing.has(key)) missing.set(key, named);
  }
  return [...missing.values()].sort();
}

/**
 * WHY A SPRINT CANNOT BE STARTED YET.
 *
 * Empty means it can. The one refusal is another sprint already running: the
 * database enforces one at a time with a unique index, and hitting that shows a
 * constraint name rather than a sentence — so the reason is said here first.
 */
export function startProblem(sprint: Sprint, all: Sprint[]): string {
  if (sprint.state === 'active') return 'That sprint is already running.';
  if (sprint.state === 'closed') return 'That sprint is finished. Plan a new one rather than reopening it.';
  const already = running(all);
  if (already) return `${already.name} is still running. Finish it before starting another.`;
  return '';
}
